import {
  Edit3LineIcon,
  IconButton,
  LockFillIcon,
  Tooltip,
  UnlockTwoToneIcon,
  XLineIcon
} from "@relex-solutions/fragments"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { NODE_LABELS } from "@/domains/visualization/constants/graph"
import { useGraphData } from "@/domains/visualization/hooks/useGraphData"
import { useUpdateParentEdge } from "@/domains/visualization/hooks/useUpdateParentEdge/useUpdateParentEdge"
import {
  selectCloseSidebar,
  selectHoveredNode,
  selectSetDisableInteractions,
  selectSetHoveredNode,
  selectSidebarLockedNode,
  selectSidebarVisible,
  selectToggleSidebarLock
} from "@/domains/visualization/store/selectors"
import { useGraphStore } from "@/domains/visualization/store/store"
import type { PositionedNode } from "@/domains/visualization/types/Graph"
import { useFeatureFlags } from "@/shared/hooks/useFeatureFlags"
import { logger } from "@/shared/utils/add/logger/logger"
import { toCapitalStringWithSpaces } from "@/shared/utils/toCapitalStringWithSpaces/toCapitalStringWithSpaces"
import { EditParentModal } from "../EditParentModal/EditParentModal"
import styles from "./NodePropertiesDisplay.module.css"
import { findNodeById, findParentFromEdges, shouldDisplayProperty } from "./NodePropertiesDisplay.utils"

type DisplayedNode = PositionedNode & {
  parentId?: string | null
}

export function NodePropertiesDisplay() {
  const { t } = useTranslation()
  const { enableEditParent } = useFeatureFlags()
  const { graphData } = useGraphData()
  const { updateParentEdgeAsync, isLoading: isUpdatingParent } = useUpdateParentEdge()
  const hoveredNode = useGraphStore(selectHoveredNode)
  const sidebarLockedNode = useGraphStore(selectSidebarLockedNode)
  const sidebarVisible = useGraphStore(selectSidebarVisible)
  const closeSidebar = useGraphStore(selectCloseSidebar)
  const setDisableInteractions = useGraphStore(selectSetDisableInteractions)
  const setHoveredNode = useGraphStore(selectSetHoveredNode)
  const toggleSidebarLock = useGraphStore(selectToggleSidebarLock)
  const [selectedNode, setSelectedNode] = useState<PositionedNode | null>(null)
  const [isSidebarHovered, setIsSidebarHovered] = useState(false)
  const [isEditParentModalOpen, setIsEditParentModalOpen] = useState(false)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Priority: locked node > hovered node
    if (sidebarLockedNode) {
      setSelectedNode(sidebarLockedNode)
    } else if (hoveredNode) {
      setSelectedNode(hoveredNode)
    }
  }, [hoveredNode, sidebarLockedNode])

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!sidebarVisible && !sidebarLockedNode) {
      clearHideTimeout()
      setIsSidebarHovered(false)
    }
  }, [sidebarVisible, sidebarLockedNode, clearHideTimeout])

  const scheduleHide = useCallback(() => {
    clearHideTimeout()

    if (!sidebarLockedNode) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsSidebarHovered(false)
      }, 300)
    }
  }, [clearHideTimeout, sidebarLockedNode])

  const handleSidebarMouseEnter = useCallback(() => {
    clearHideTimeout()
    setIsSidebarHovered(true)
  }, [clearHideTimeout])

  const handleSidebarMouseLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const handleClose = () => {
    clearHideTimeout()
    setIsSidebarHovered(false)
    closeSidebar()
  }

  const handleLockToggle = useCallback(() => {
    if (selectedNode) {
      toggleSidebarLock(selectedNode)
    }
  }, [toggleSidebarLock, selectedNode])

  const handleEditParentClick = useCallback(() => {
    setDisableInteractions(true)
    setIsEditParentModalOpen(true)
  }, [setDisableInteractions])

  const handleEditParentModalClose = useCallback(() => {
    setDisableInteractions(false)
    setIsEditParentModalOpen(false)
  }, [setDisableInteractions])

  const handleEditParentConfirm = useCallback(
    async (newParentId: string | null) => {
      if (!selectedNode || selectedNode.label !== NODE_LABELS.PLAN_CUSTOMER) {
        setDisableInteractions(false)
        setIsEditParentModalOpen(false)
        return
      }

      try {
        const oldParentId = findParentFromEdges(selectedNode.id, graphData.edges)
        const newParentNode = findNodeById(newParentId, graphData.nodes)
        await updateParentEdgeAsync({
          selectedNodeId: selectedNode.id,
          selectedNodeName: selectedNode.name,
          newParentId,
          oldParentId,
          newParentName: newParentNode?.name
        })

        setHoveredNode(null)
        closeSidebar()
        setDisableInteractions(false)
        setIsEditParentModalOpen(false)
      } catch (error) {
        logger.error("Failed to update parent relationship:", error)
        setDisableInteractions(false)
      }
    },
    [
      selectedNode,
      graphData.nodes,
      graphData.edges,
      updateParentEdgeAsync,
      setHoveredNode,
      closeSidebar,
      setDisableInteractions
    ]
  )

  const displayedNode = useMemo((): DisplayedNode | null => {
    if (!selectedNode) return null

    if (selectedNode.label === NODE_LABELS.PLAN_CUSTOMER) {
      const parentFromEdge = findParentFromEdges(selectedNode.id, graphData.edges)
      const parentNode = findNodeById(parentFromEdge, graphData.nodes)
      return {
        ...selectedNode,
        parent: parentNode?.name ?? "N/A",
        parentId: parentFromEdge || undefined
      }
    }

    return selectedNode
  }, [selectedNode, graphData.edges, graphData.nodes])

  const shouldShow = sidebarVisible || (!sidebarLockedNode && !!hoveredNode) || (!sidebarLockedNode && isSidebarHovered)

  if (!displayedNode) return null
  return (
    <aside
      data-testid="node-properties-display"
      data-block-pan="true"
      className={`${styles.overlay} ${shouldShow ? styles.visible : styles.hidden}`}
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      {shouldShow && (
        <Tooltip
          placement="top"
          title={
            sidebarLockedNode
              ? t("visualization.nodePropertiesDisplay.sidebarLocked")
              : t("visualization.nodePropertiesDisplay.sidebarUnlocked")
          }
        >
          <IconButton
            aria-label={
              sidebarLockedNode
                ? t("visualization.nodePropertiesDisplay.unlockSidebar")
                : t("visualization.nodePropertiesDisplay.lockSidebar")
            }
            borderRadius="full"
            size="medium"
            variant="outline"
            className={styles.lockButton}
            onClick={handleLockToggle}
          >
            {sidebarLockedNode ? (
              <LockFillIcon
                style={{
                  color: "var(--fr-color-icon)"
                }}
              />
            ) : (
              <UnlockTwoToneIcon
                style={{
                  "--fr-icon-primary-color": "var(--fr-color-icon)",
                  "--fr-icon-secondary-color": "var(--fr-color-icon)"
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      )}
      <div className={styles.header}>
        <div>
          <h3 className={`${styles.nodeTitle} fr-text-400-strong`}>{displayedNode.name}</h3>
          <div className={`${styles.nodeSubtitle} fr-text-250`}>
            {displayedNode.label.toUpperCase()} · ID: {displayedNode.id}
          </div>
        </div>
        <IconButton
          aria-label={t("general.close")}
          borderRadius="rounded"
          size="small"
          variant="outline"
          className={styles.closeButton}
          onClick={handleClose}
        >
          <XLineIcon />
        </IconButton>
      </div>

      <div className={`${styles.sectionTitle} fr-text-300-strong`}>
        {t("visualization.nodePropertiesDisplay.nodeProperties")}
      </div>

      <div className={`${styles.propertiesContainer} fr-text-250`}>
        {Object.entries(displayedNode)
          .filter(([key, value]) => shouldDisplayProperty(key, value as string))
          .map(([key, value]) => (
            <div key={key} className={styles.propertyRow}>
              <span className={styles.propertyLabel}>{toCapitalStringWithSpaces(key)}:</span>
              <div className={styles.propertyValueContainer}>
                <span className={styles.propertyValue}>{value.toString()}</span>
                {key.toLowerCase() === "parent" &&
                  displayedNode.label === NODE_LABELS.PLAN_CUSTOMER &&
                  enableEditParent && (
                    <Tooltip title={t("visualization.nodePropertiesDisplay.editParent")}>
                      <IconButton
                        aria-label={t("visualization.nodePropertiesDisplay.editParent")}
                        size="small"
                        variant="ghost"
                        className={styles.editButton}
                        onClick={handleEditParentClick}
                      >
                        <Edit3LineIcon aria-hidden="true" />
                      </IconButton>
                    </Tooltip>
                  )}
              </div>
            </div>
          ))}
      </div>

      {displayedNode.label === NODE_LABELS.PLAN_CUSTOMER && displayedNode.parent && (
        <EditParentModal
          key={displayedNode.parentId}
          open={isEditParentModalOpen}
          currentParent={{
            id: displayedNode.parentId || undefined,
            name: displayedNode.parent || undefined
          }}
          nodeName={displayedNode.name}
          onClose={handleEditParentModalClose}
          onConfirm={handleEditParentConfirm}
          isLoading={isUpdatingParent}
        />
      )}
    </aside>
  )
}
